import { TIMING } from '../constants';

/** Breaks seconds into hours and minutes. */
function getHoursAndMinutes(seconds: number): { h: number; m: number } {
  return {
    h: Math.floor(seconds / TIMING.HOUR_SECONDS),
    m: Math.floor((seconds % TIMING.HOUR_SECONDS) / 60),
  };
}

/**
 * Compact duration: 5400 → "1h 30m", 1800 → "30m", 3600 → "1h".
 * Use for card/badge UI. Distinct from scheduleUtils.formatDuration (verbose form).
 */
export function fmtDurationCompact(seconds: number): string {
  const { h, m } = getHoursAndMinutes(seconds);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Stable YYYY-MM-DD key for a timestamp, using local calendar date. */
export function toDateKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Parse a YYYY-MM-DD key to a Date at local noon.
 * Noon avoids the timezone edge-case where midnight UTC may fall on the previous calendar day.
 */
export function dateKeyToDate(key: string): Date {
  return new Date(`${key}T12:00:00`);
}

export function formatRelativeDate(scheduledAt: number): string {
  const days = Math.ceil((scheduledAt - Date.now()) / TIMING.DAY_MS);
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

export function formatElapsedLabel(elapsedSeconds: number): string | null {
  if (elapsedSeconds <= 0) return null;
  if (elapsedSeconds >= TIMING.HOUR_SECONDS) {
    const { h, m } = getHoursAndMinutes(elapsedSeconds);
    return `${h}h ${m}m in`;
  }
  return elapsedSeconds >= 60 ? `${Math.floor(elapsedSeconds / 60)}m in` : null;
}

export function formatElapsedDuration(elapsedSeconds: number): string {
  if (elapsedSeconds >= TIMING.HOUR_SECONDS) {
    const h = Math.floor(elapsedSeconds / TIMING.HOUR_SECONDS);
    return `${h} hour${h !== 1 ? "s" : ""}`;
  }
  return `${Math.floor(elapsedSeconds / 60)} minutes`;
}

export function formatScheduledDateLabel(scheduledAt: number): string {
  return new Date(scheduledAt).toLocaleDateString("en-US", {
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
  });
}
